import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ScanBarcode, Timer, BellRing, Sprout } from "lucide-react";
import { FadeUp } from "./Reveal";

const IMG_PRODUCE =
  "https://images.unsplash.com/photo-1542838132-92c53300491e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMG9yZ2FuaWMlMjBwcm9kdWNlJTIwc3BvdGxpZ2h0fGVufDB8fHx8MTc4NjY3MDE2N3ww&ixlib=rb-4.1.0&q=85&w=900";
const IMG_KITCHEN =
  "https://images.unsplash.com/photo-1556911220-bff31c812dba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxtaW5pbWFsJTIwa2l0Y2hlbiUyMGluZ3JlZGllbnRzJTIwbW9kZXJufGVufDB8fHx8MTc4NjY3MDE2N3ww&ixlib=rb-4.1.0&q=85&w=900";

const ParallaxImage = ({ src, alt, testid, frameClass }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className={`overflow-hidden border border-[#EAE5D9]/20 ${frameClass}`}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y }}
        className="w-full h-full object-cover scale-110"
        data-testid={testid}
      />
    </div>
  );
};

const ChapterNumber = ({ num }) => (
  <span
    aria-hidden="true"
    className="block font-serif leading-none text-[6rem] md:text-[10rem] select-none"
    style={{ WebkitTextStroke: "1px rgba(234,229,217,0.45)", color: "transparent" }}
  >
    {num}
  </span>
);

const AlertCard = ({ className, tone, title, body, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay }}
    whileHover={{ rotate: 0, scale: 1.02 }}
    className={`rounded-2xl p-6 shadow-2xl ${className} ${
      tone === "dark" ? "bg-[#16783F] text-[#F7F6F2]" : "bg-[#F7F6F2] text-[#111111]"
    }`}
  >
    <div className="flex items-center gap-3">
      <span
        className={`h-10 w-10 rounded-xl flex items-center justify-center ${
          tone === "dark" ? "bg-[#F7F6F2]/15" : "bg-[#1F9D55]"
        }`}
      >
        <Sprout className="h-5 w-5 text-[#F7F6F2]" />
      </span>
      <div>
        <p className={`text-[10px] uppercase tracking-[0.2em] ${tone === "dark" ? "text-[#F7F6F2]/70" : "text-[#111111]/50"}`}>
          My Fresh Tracker · now
        </p>
        <p className="font-medium">{title}</p>
      </div>
    </div>
    <p className={`mt-3 text-sm ${tone === "dark" ? "text-[#F7F6F2]/85" : "text-[#111111]/70"}`}>{body}</p>
  </motion.div>
);

const CHAPTERS = [
  {
    num: "01",
    icon: ScanBarcode,
    title: "Scan it in.",
    copy: "Snap a barcode or type it once. Every item is logged the moment it crosses your kitchen threshold — no spreadsheets, no mental math, no forgotten yoghurt at the back of the shelf.",
  },
  {
    num: "02",
    icon: Timer,
    title: "Watch the clock.",
    copy: "Live countdowns on everything you own. One glance tells you what's fresh, what's soon, and what should be tonight's dinner.",
  },
  {
    num: "03",
    icon: BellRing,
    title: "Get nudged, not buried.",
    copy: "Gentle, well-timed alerts before anything expires. Tuned to your routine — never spammy, never shrill.",
  },
];

export default function Features() {
  return (
    <section
      id="features"
      className="bg-[#0A130B] text-[#EAE5D9] px-6 md:px-12 py-24 md:py-40"
      data-testid="features-section"
    >
      <div className="max-w-[1400px] mx-auto">
        <FadeUp>
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-[#F2C94C]">
            The features — a manifesto in three chapters
          </p>
          <h2 className="font-serif font-light tracking-tight text-4xl md:text-6xl mt-6 max-w-3xl">
            A fresher kitchen, <em className="text-[#1F9D55]">kept honest.</em>
          </h2>
        </FadeUp>

        <div className="mt-24 md:mt-36 space-y-32 md:space-y-44">
          <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center" data-testid="feature-chapter-1">
            <div className="md:col-span-5">
              <FadeUp>
                <ParallaxImage
                  src={IMG_PRODUCE}
                  alt="Fresh organic vegetables at a market stall"
                  testid="feature-image-1"
                  frameClass="rounded-t-full h-[380px] md:h-[520px]"
                />
              </FadeUp>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <FadeUp delay={0.1}>
                <ChapterNumber num={CHAPTERS[0].num} />
                <div className="flex items-center gap-3 mt-2">
                  {(() => {
                    const Icon = CHAPTERS[0].icon;
                    return <Icon className="h-5 w-5 text-[#1F9D55]" />;
                  })()}
                  <h3 className="font-serif text-3xl md:text-5xl tracking-tight">{CHAPTERS[0].title}</h3>
                </div>
                <p className="mt-6 text-base md:text-lg leading-relaxed text-[#EAE5D9]/70 max-w-md">
                  {CHAPTERS[0].copy}
                </p>
              </FadeUp>
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center" data-testid="feature-chapter-2">
            <div className="md:col-span-6 order-2 md:order-1">
              <FadeUp delay={0.1}>
                <ChapterNumber num={CHAPTERS[1].num} />
                <div className="flex items-center gap-3 mt-2">
                  {(() => {
                    const Icon = CHAPTERS[1].icon;
                    return <Icon className="h-5 w-5 text-[#1F9D55]" />;
                  })()}
                  <h3 className="font-serif text-3xl md:text-5xl tracking-tight">{CHAPTERS[1].title}</h3>
                </div>
                <p className="mt-6 text-base md:text-lg leading-relaxed text-[#EAE5D9]/70 max-w-md">
                  {CHAPTERS[1].copy}
                </p>
              </FadeUp>
            </div>
            <div className="md:col-span-5 md:col-start-8 order-1 md:order-2">
              <FadeUp>
                <ParallaxImage
                  src={IMG_KITCHEN}
                  alt="A calm, modern kitchen with fresh ingredients on the counter"
                  testid="feature-image-2"
                  frameClass="rounded-[2rem] h-[380px] md:h-[520px]"
                />
              </FadeUp>
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center" data-testid="feature-chapter-3">
            <div className="md:col-span-5">
              <div className="relative pb-10">
                <AlertCard
                  className="rotate-[3deg]"
                  title="Milk expires tomorrow"
                  body="Plan breakfast around it — or freeze it tonight."
                  delay={0}
                />
                <AlertCard
                  className="rotate-[-4deg] -mt-4 ml-6 md:ml-12"
                  tone="dark"
                  title="Spinach: 2 days left"
                  body="Perfect night for a green pasta."
                  delay={0.15}
                />
              </div>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <FadeUp delay={0.1}>
                <ChapterNumber num={CHAPTERS[2].num} />
                <div className="flex items-center gap-3 mt-2">
                  {(() => {
                    const Icon = CHAPTERS[2].icon;
                    return <Icon className="h-5 w-5 text-[#1F9D55]" />;
                  })()}
                  <h3 className="font-serif text-3xl md:text-5xl tracking-tight">{CHAPTERS[2].title}</h3>
                </div>
                <p className="mt-6 text-base md:text-lg leading-relaxed text-[#EAE5D9]/70 max-w-md">
                  {CHAPTERS[2].copy}
                </p>
              </FadeUp>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
